import React, { createContext, useCallback, useContext, useMemo, useState } from "react";
import { shallowEqual, useSelector } from "react-redux";
import QrScannerModal from "./QrScannerModal";

// ONE reusable scanner for the whole app. Any screen asks for it with
// `openQrScanner({ title, onAsset })`:
//   - with `onAsset`: the scan (or Asset ID) is resolved through the normal
//     server-authorised scan endpoint and the caller receives an asset
//     context ({ _id, assetID, name, site, ... }) - never the qrToken. What
//     the user may then DO with that asset is still decided by the module
//     receiving it (and, ultimately, the backend).
//   - without `onAsset` (the global "Scan QR" button): behaves exactly as
//     before - navigates to /scan/:qrToken, which opens Asset 360.
const QrScannerContext = createContext({ openQrScanner: null });

export const useQrScanner = () => useContext(QrScannerContext);

// The same eligibility rule the scan endpoint applies (viewAsset or
// viewAssignedAsset), plus an optional action permission the calling
// workflow needs - scanning never substitutes for it.
export const useCanScanQr = requires => {
    const { user } = useSelector(state => state.auth.user || {}, shallowEqual);
    const permissions = user && user.userRole && user.userRole.permission ? user.userRole.permission : [];
    const has = value => !!(user && user.super) || permissions.some(p => p && p.value === value);
    return (has("viewAsset") || has("viewAssignedAsset")) && (!requires || has(requires));
};

export function QrScannerProvider({ children }) {
    const [request, setRequest] = useState(null);
    const openQrScanner = useCallback(options => setRequest(options || {}), []);
    const close = useCallback(() => setRequest(null), []);
    const value = useMemo(() => ({ openQrScanner }), [openQrScanner]);

    return (
        <QrScannerContext.Provider value={value}>
            {children}
            <QrScannerModal show={!!request} request={request} onHide={close} />
        </QrScannerContext.Provider>
    );
}
